/**
 * ProtectedRoute - Route protection component for authenticated areas
 * Handles authentication checks and redirects based on user and profile state
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { LoadingSpinner } from '@components/ui/LoadingSpinner';

// ============================================================================
// Types
// ============================================================================

export interface ProtectedRouteProps {
  children: React.ReactNode;
  requireProfile?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireProfile = true,
}) => {
  const { user, profile, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner message="Verificando autenticación..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Redirect to profile setup if profile is required but missing
  if (requireProfile && !profile) {
    return <Navigate to="/auth/profile-setup" replace />;
  }

  // User is authenticated and has required profile (if needed)
  return <>{children}</>;
};

export default ProtectedRoute;
